import * as api from 'lib/api';
import { v4 as uuid } from 'uuid';
import { fetchTimesheetMessages, watchTimesheetMessages, sendTimesheetMessage, watchPaymentRuns as watchPayRuns, fetchPaymentRunSummaries as fetchPaymentRunSummariesApi } from 'lib/api/timesheets';
import { updateBooking } from '../reducers/jobs';
import * as timesheetsReducers from '../reducers/timesheets';
import * as CONSTANTS from '../routes/Timesheets/timesheet-constants';

export function approveTimesheet(timesheetKey, shiftKey, candidateKey) {
  return async (dispatch, getState) => {
    dispatch(timesheetsReducers.isApprovingTimesheets(timesheetKey));

    const response = await api.post('timesheets/approve', { timesheetKey });
    if (response.status > 200) {
      dispatch(timesheetsReducers.timesheetsError('Something went wrong. Please try again.'));
      return;
    }

    const { timesheets } = getState();
    const filterValue = timesheets.timesheetFilters.status;

    const updatedTimesheets = timesheets.timesheets.map((timesheet) => {
      if (timesheet.key === timesheetKey) {
        // Remove if updated timesheet no longer matches current filters
        if (filterValue === CONSTANTS.STATUSES.REJECTED || filterValue === CONSTANTS.STATUSES.PENDING_APPROVAL) {
          return null;
        }
        // Otherwise update timesheet in list
        return { ...timesheet, status: 'approved' };
      }
      // else do nothing
      return timesheet;
    }).filter(Boolean);

    dispatch(updateBooking(shiftKey, candidateKey, { timesheetStatus: 'approved' }));

    dispatch(timesheetsReducers.updateTimesheets(updatedTimesheets));
  };
}

export function rejectTimesheet(timesheetKey, shiftKey, candidateKey, rejectReason) {
  return async (dispatch, getState) => {
    dispatch(timesheetsReducers.isRejectingTimesheets(timesheetKey));

    const response = await api.post('timesheets/reject', { rejectReason, timesheetKey });
    if (response.status > 200) {
      dispatch(timesheetsReducers.timesheetsError('Something went wrong. Please try again.'));
      return;
    }

    const { timesheets } = getState();
    const filterValue = timesheets.timesheetFilters.status;

    const updatedTimesheets = timesheets.timesheets.map((timesheet) => {
      if (timesheet.key === timesheetKey) {
        // Remove if updated timesheet no longer matches current filters
        if (filterValue === CONSTANTS.STATUSES.APPROVED || filterValue === CONSTANTS.STATUSES.PENDING_APPROVAL) {
          return null;
        }
        // Otherwise update timesheet in list
        return { ...timesheet, status: 'rejected' };
      }
      // else do nothing
      return timesheet;
    }).filter(Boolean);

    dispatch(updateBooking(shiftKey, candidateKey, { timesheetStatus: 'rejected' }));

    dispatch(timesheetsReducers.updateTimesheets(updatedTimesheets));
  };
}

function calculateTimesheetsTotalCount({ currentTotal, offset, limit, timesheetsLength }) {

  // If both offset and total count are zero (first fetch)
  // or if limit is more than currentTotal, then return timesheets length from response
  if ((offset === 0 && currentTotal === 0) || (limit > currentTotal)) return timesheetsLength;

  // If offset >= currentTotal, return timesheetsLength from response + currentTotal
  if (offset >= currentTotal) return currentTotal + timesheetsLength;

  // Otherwise return currentTotal
  return currentTotal;
}

export function fetchTimesheets() {
  return async (dispatch, getState) => {
    const requestId = uuid();
    dispatch(timesheetsReducers.fetchTimesheets(requestId));
    const timesheets = getState().timesheets;
    const {
      candidate,
      adminGroup,
      status,
      speciality,
      site,
      service,
    } = timesheets.timesheetFilters;

    const { timesheetsPaginationsOffset, timesheetsPaginationLimit, timesheetsTotalCount } = timesheets;

    const rows = [];
    let moreRowsAvailable = false;
    try {
      while (rows.length < timesheetsPaginationLimit) {
        // Each chunk determines the next offset and whether another request is needed.
        // eslint-disable-next-line no-await-in-loop
        const response = await api.post('timesheets/search-admin', {
          status,
          includeArchived: timesheets.includeArchived ?? false,
          candidate,
          adminGroup,
          speciality,
          site,
          service,
          offset: timesheetsPaginationsOffset + rows.length,
          limit: Math.min(200, timesheetsPaginationLimit - rows.length),
        });
        // A response from a page we have left must not replace the current list.
        if (getState().timesheets.timesheetsRequestId !== requestId) return;
        if (!Array.isArray(response?.body?.timesheets)) throw new Error('Invalid admin timesheet search response');
        rows.push(...response.body.timesheets);
        moreRowsAvailable = response.body.moreRowsAvailable;
        if (!moreRowsAvailable || response.body.timesheets.length === 0) break;
      }
    } catch (error) {
      if (getState().timesheets.timesheetsRequestId === requestId) {
        dispatch(timesheetsReducers.timesheetsError('Something went wrong. Please try again.'));
      }
      return;
    }
    const timesheetsLength = rows.length;

    const updatedTotalCount = calculateTimesheetsTotalCount({
      currentTotal: timesheetsTotalCount,
      offset: timesheetsPaginationsOffset,
      limit: timesheetsPaginationLimit,
      timesheetsLength,
    });

    dispatch(timesheetsReducers.addTimesheets(rows, moreRowsAvailable, updatedTotalCount));

  };
}

export function fetchTimesheetRejectReasons() {
  return async (dispatch) => {
    dispatch(timesheetsReducers.fetchingTimesheetRejectReasons());
    const response = await api.get('timesheets/reject-reasons');
    if (!response.body.success) {
      dispatch(timesheetsReducers.timesheetsError('Something went wrong. Please try again.'));
    } else {
      dispatch(timesheetsReducers.addTimesheetRejectReasons(response?.body?.timesheetRejectReasons));
    }
  };
}

export function fetchCandidateRatingCategories(candidateKey) {
  return async (dispatch) => {
    dispatch(timesheetsReducers.fetchingCandidateRatingCategories());
    const response = await api.get('rating/categories', { candidateKey });
    if (!response.body.success) {
      dispatch(timesheetsReducers.timesheetsError('Something went wrong. Please try again.'));
    } else {
      dispatch(timesheetsReducers.addCandidateRatingCategories(response?.body?.orgRatingCategories));
    }
  };
}

export function submitCandidateRatings({ ratings, comment, shiftKey, candidateKey }) {
  return async (dispatch) => {
    dispatch(timesheetsReducers.submittingCandidateRatings());
    const response = await api.post('candidate-rating/submit', { ratings, comment: comment || null, shiftKey, candidateKey });
    if (!response?.body?.success) {
      dispatch(timesheetsReducers.timesheetsError('Something went wrong. Please try again.'));
    } else {
      const submittedRatings = {};
      ratings.forEach((rating) => {
        submittedRatings[rating.key] = rating.rating;
      });
      dispatch(updateBooking(shiftKey, candidateKey, { submittedRatings, submittedReview: comment }));
      dispatch(timesheetsReducers.submitRatingsSuccess());
    }
  };
}

export function watchMessages(timesheetKey) {
  return dispatch => {
    dispatch(timesheetsReducers.fetchTimesheetMessages(timesheetKey));
    let stopped = false;
    let receivedMessages = false;
    let receivedLiveUpdate = false;
    let liveFailed = false;
    let subscription;
    const fail = message => {
      if (!stopped) dispatch(timesheetsReducers.timesheetMessagesError(message));
    };
    const timeout = setTimeout(() => {
      if (!receivedMessages) fail('Unable to load messages and audit trail. Please retry.');
    }, 15000);
    const accept = messages => {
      if (stopped) return;
      receivedMessages = true;
      clearTimeout(timeout);
      dispatch(timesheetsReducers.addTimesheetMessages(messages));
      if (liveFailed) fail('Live updates are unavailable. Retry to reconnect.');
    };
    const onLiveError = () => {
      liveFailed = true;
      if (receivedMessages) fail('Live updates are unavailable. Retry to reconnect.');
    };

    // Read the initial snapshot over HTTP so a reconnecting socket cannot leave
    // the panel waiting forever. Live updates take precedence over that snapshot.
    fetchTimesheetMessages(timesheetKey).then(messages => {
      if (!receivedLiveUpdate) accept(messages);
    }).catch(() => {
      if (!receivedMessages) {
        clearTimeout(timeout);
        fail('Unable to load messages and audit trail. Please retry.');
      }
    });
    try {
      subscription = watchTimesheetMessages({
        timesheetKey,
        callback: res => {
          if (stopped) return;
          const messages = res?.data?.timesheet_timesheet_messages;
          if (res?.errors?.length || !Array.isArray(messages)) { onLiveError(); return; }
          receivedLiveUpdate = true;
          liveFailed = false;
          accept(messages);
        },
        error: onLiveError,
      });
    } catch (err) { onLiveError(); }

    return {
      unsubscribe: () => {
        stopped = true;
        clearTimeout(timeout);
        subscription?.unsubscribe();
      },
    };
  };
}

export function fetchPaymentRunSummaries() {
  return async (dispatch) => {
    dispatch(timesheetsReducers.fetchingPaymentRuns());
    try {
      const response = await fetchPaymentRunSummariesApi();
      dispatch(timesheetsReducers.setPaymentRuns(response.paymentRunSummaries));
    } catch (err) {
      dispatch(timesheetsReducers.paymentRunListViewError(err.message));
    }
  };
}

export function watchPaymentRuns() {
  return (dispatch) => {
    dispatch(timesheetsReducers.fetchingPaymentRuns());
    return watchPayRuns({
      callback: paymentRuns => dispatch(timesheetsReducers.setPaymentRuns(paymentRuns)),
      error: err => dispatch(timesheetsReducers.paymentRunListViewError(err.message)),
    });
  };
}

export function sendMessage(timesheetKey, messageText) {
  return async (dispatch) => {
    const payload = { messageText, timesheetKey };
    dispatch(timesheetsReducers.sendingMessage());
    const response = await sendTimesheetMessage(payload);

    if (!response.body.success) {
      dispatch(timesheetsReducers.timesheetsError('Something went wrong. Please try again.'));
    } else {
      dispatch(timesheetsReducers.sendMessageSuccess());
    }
  };
}
